import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useRobots } from "@/components/seo/Seo";
import { Link, Navigate } from "react-router-dom";
import {
  Calendar, CreditCard, LayoutDashboard, Loader2, LogOut, MapPin, Plus,
  Search, Settings, User, Users, Wallet, BarChart3,
} from "lucide-react";
import { useBusinessAuth } from "@/lib/businessAuth";
import { useBiz } from "@/lib/businessI18n";
import bizApi from "@/lib/businessApi";
import MilesLogo from "@/components/Logo";
import Overview from "@/components/business/Overview";
import MissionHub from "@/components/business/MissionHub";
import Missions from "@/components/business/Missions";
import Guests from "@/components/business/Guests";
import Places from "@/components/business/Places";
import Billing from "@/components/business/Billing";
import Commissions from "@/components/business/Commissions";
import Reports from "@/components/business/Reports";
import Team from "@/components/business/Team";
import SettingsPanel from "@/components/business/Settings";
import CommandPalette from "@/components/business/CommandPalette";

const NAV_ITEMS = [
  { id: "overview", icon: LayoutDashboard, perm: null },
  { id: "new_mission", icon: Plus, perm: "create_bookings" },
  { id: "missions", icon: Calendar, perm: null },
  { id: "guests", icon: Users, perm: "see_guest_data" },
  { id: "places", icon: MapPin, perm: null },
  { id: "billing", icon: CreditCard, perm: "see_invoices" },
  { id: "commissions", icon: Wallet, perm: "see_commissions", needsCommissions: true },
  { id: "reports", icon: BarChart3, perm: "export_data" },
  { id: "team", icon: User, perm: "manage_users" },
  { id: "settings", icon: Settings, perm: null },
];

export default function BusinessDashboard() {
  const { t } = useBiz();
  useRobots("noindex, nofollow");
  const { status, user, org, logout, setOrg } = useBusinessAuth();
  const [tab, setTab] = useState("overview");
  const [prefill, setPrefill] = useState(null);
  const [builderKey, setBuilderKey] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const perms = useMemo(() => new Set(user?.permissions || []), [user]);
  const commissionsOn = Boolean(org?.commercial?.commission_enabled);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // retour de Stripe Checkout (carte / lien de paiement)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    if (!payment) return;
    if (payment === "success") toast.success(t("pay.success"));
    if (payment === "cancelled") toast.info(t("pay.cancelled"));
    window.history.replaceState({}, "", window.location.pathname);
  }, [t]);

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cloud" data-testid="dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-miles" strokeWidth={1.5} />
      </div>
    );
  }
  if (status === "out") return <Navigate to="/business/connexion" replace />;

  const nav = NAV_ITEMS.filter((item) =>
    (!item.perm || perms.has(item.perm)) && (!item.needsCommissions || commissionsOn));
  const activeTab = nav.some((item) => item.id === tab) ? tab : "overview";

  const go = (id, pre = null) => {
    if (id === "new_mission") setBuilderKey((k) => k + 1);  // remount → builder réinitialisé
    setPrefill(pre);
    setTab(id);
  };
  const openMission = (missionId) => go("missions", { missionId });
  const bookForGuest = (guest) => go("new_mission", { guest });
  const duplicateMission = (payload) => go("new_mission", { duplicate: payload });

  return (
    <div className="min-h-screen bg-cloud text-ink" data-testid="business-dashboard">
      <div className="flex min-h-screen">
        <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-ink/5 bg-white lg:flex" data-testid="dash-sidebar">
          <div className="flex h-[72px] items-center border-b border-ink/5 px-6">
            <Link to="/" className="flex items-center gap-2.5" data-testid="dash-logo" aria-label="MILES Business">
              <MilesLogo variant="blue" className="h-[18px]" testId="dash-logo-img" />
              <span className="text-[15px] font-bold text-miles">Business</span>
            </Link>
          </div>
          <nav className="flex-1 space-y-0.5 overflow-y-auto p-4" data-testid="dash-nav">
            {nav.map((item) => (
              <button key={item.id} type="button" onClick={() => go(item.id)} data-testid={`dash-nav-${item.id}`}
                className={`flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === item.id ? "bg-miles-ice text-miles" : "text-slate hover:bg-cloud hover:text-ink"
                }`}>
                <item.icon className="h-4 w-4" strokeWidth={1.5} /> {t(`nav.${item.id}`)}
              </button>
            ))}
          </nav>
          <div className="border-t border-ink/5 p-4">
            <div className="rounded-2xl bg-miles-deep p-4" data-testid="dash-org-card">
              <p className="truncate text-xs font-bold text-white">{org.name}</p>
              <p className="mt-1 truncate text-[10px] text-miles-sky/70">{user.name} · {t(`team.role.${user.role}`)}</p>
            </div>
          </div>
        </aside>

        <div className="flex-1 min-w-0 w-full lg:pl-60">
          <header className="sticky top-0 z-30 flex h-[72px] items-center gap-3 border-b border-ink/5 bg-white/85 px-5 backdrop-blur-xl sm:px-8" data-testid="dash-header">
            <button type="button" onClick={() => setPaletteOpen(true)} data-testid="dash-search"
              className="hidden h-10 flex-1 max-w-md items-center gap-2.5 rounded-xl border border-ink/8 bg-cloud px-4 text-left text-[13px] text-slate transition-colors hover:border-miles/30 sm:flex">
              <Search className="h-3.5 w-3.5" strokeWidth={2} />
              <span className="truncate">{t("hub.search_placeholder")}</span>
              <kbd className="ml-auto rounded-md border border-ink/10 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate">⌘K</kbd>
            </button>
            <div className="min-w-0 sm:hidden">
              <p className="truncate text-sm font-bold text-ink" data-testid="dash-hello">
                {t("hub.hello").replace("{name}", user.name.split(" ")[0])}
              </p>
              <p className="truncate text-[11px] text-slate">{org.name}</p>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <Link to="/business" className="hidden text-xs font-semibold text-slate transition-colors hover:text-ink sm:block" data-testid="dash-back-site">
                {t("hub.back_site")}
              </Link>
              <button type="button" onClick={logout} data-testid="dash-logout"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate transition-colors hover:text-red-500">
                <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("hub.logout")}
              </button>
              {perms.has("create_bookings") && (
                <button type="button" onClick={() => go("new_mission")} data-testid="dash-new-mission-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-miles px-5 py-2.5 text-xs font-semibold text-white shadow-[0_8px_24px_rgba(36,87,255,0.3)] transition-all hover:-translate-y-0.5 hover:bg-miles-dark">
                  <Plus className="h-3.5 w-3.5" strokeWidth={2} /> {t("hub.new")}
                </button>
              )}
            </div>
          </header>

          <div className="sticky top-[72px] z-20 flex gap-1 overflow-x-auto border-b border-ink/5 bg-white/85 px-5 py-2 backdrop-blur-xl lg:hidden" data-testid="dash-nav-mobile">
            {nav.map((item) => (
              <button key={item.id} type="button" onClick={() => go(item.id)} data-testid={`dash-nav-mobile-${item.id}`}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                  activeTab === item.id ? "bg-miles text-white" : "text-slate"
                }`}>
                {t(`nav.${item.id}`)}
              </button>
            ))}
          </div>

          <main className="p-5 pb-24 sm:p-8 lg:pb-8" data-testid="dash-main">
            {activeTab === "overview" && <Overview onNavigate={go} onOpenMission={openMission} canCreate={perms.has("create_bookings")} />}
            {activeTab === "new_mission" && <MissionHub key={builderKey} org={org} prefill={prefill}
              onDone={(id) => { setPrefill(null); openMission(id); }} />}
            {activeTab === "missions" && <Missions prefill={prefill} onClearPrefill={() => setPrefill(null)}
              onDuplicate={duplicateMission} onNewReturn={(p) => go("new_mission", { duplicate: p })} />}
            {activeTab === "guests" && <Guests onBookFor={bookForGuest} />}
            {activeTab === "places" && <Places />}
            {activeTab === "billing" && <Billing />}
            {activeTab === "commissions" && <Commissions org={org} />}
            {activeTab === "reports" && <Reports />}
            {activeTab === "team" && <Team currentUser={user} />}
            {activeTab === "settings" && <SettingsPanel org={org} onSaved={setOrg} />}
          </main>
        </div>
      </div>

      {perms.has("create_bookings") && (
        <button type="button" onClick={() => go("new_mission")} data-testid="dash-mobile-new-mission"
          className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-miles text-white shadow-[0_10px_30px_rgba(36,87,255,0.4)] transition-transform hover:scale-105 lg:hidden">
          <Plus className="h-6 w-6" strokeWidth={2} />
        </button>
      )}

      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} onNavigate={go} onOpenMission={openMission} />}
    </div>
  );
}
